import React, { useEffect, useMemo, useState } from "react";
import Icons from "../icons";
import { preconditionLineNote, stepToEnglish, stepToScript } from "../stepEnglish";
import { EVIDENCE_POINTER, isObsolete, JiraStatusBadge, PriorityBadge, statusPhrase, TRIAL_LABEL, trialWarning } from "../jiraBadges";
import { ActivityButton } from "./ActivityPanel";
import ArtifactScriptEditor from "./ArtifactScriptEditor";
import ConfirmModal from "./ConfirmModal";
import { DIALOG_HEIGHT } from "./dialogSize";
import ErrorDialog from "./ErrorDialog";
import Loader from "./Loader";
import RaiseBugModal from "./RaiseBugModal";
import RunVariablesDialog from "./RunVariablesDialog";
import { useToast } from "./Toast";

/* Test Runs page, levels 2 and 3 (level 1 — the epics — lives in
   ExecutionsView):

     Tests column — every saved test of the epic, grouped by story, with its
                    last result, how many runs it has and its bug; filter +
                    search.
     Runs panel   — the chosen test: one card per TRIAL execution. A trial
                    builds and checks a script: it runs here, writes nothing
                    to Jira and is never evidence (that is For you › Test
                    Runs). A finished trial can be run again; a failure can
                    still raise a Bug.                                      */

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";
export const ACTIVE_RUN = new Set(["queued", "running"]);

/* One header height for the screen's three columns (Epics, Artifacts, Trial
   runs), whatever each holds: a count, a refresh button, a line of text. */
export const COLUMN_HEAD = "flex h-16 flex-none flex-col justify-center border-b border-slate-200 bg-slate-100/70 px-4";
export const OTHER_RUNS = "__other_runs__";
/* The Epics column's "No story" row: Artifacts filed under no story. */
export const NO_STORY = "__no_story__";

/* The story (or NO_STORY) of an Artifacts-column id (`story|test`, see
   buildTests), or null. */
export function storyOfTest(id) {
  if (!id || id === OTHER_RUNS || !String(id).includes("|")) return null;
  return String(id).split("|")[0] || NO_STORY;
}

export function plural(n, word, many = `${word}s`) {
  return `${n} ${n === 1 ? word : many}`;
}

export function initials(name) {
  const parts = String(name || "").trim().split(/[\s._@-]+/).filter(Boolean);
  return ((parts[0]?.[0] || "") + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase() || "?";
}

/* ------------------------------------------------------------------ */
/*  Status look                                                        */
/* ------------------------------------------------------------------ */
export const TONES = {
  new: { label: "New · not executed", pill: "bg-blue-50 text-blue-700 ring-blue-600/20", dot: "bg-blue-500", solid: "bg-blue-500", Icon: Icons.Spark },
  queued: { label: "Queued", pill: "bg-slate-100 text-slate-600 ring-slate-500/20", dot: "bg-slate-400", solid: "bg-slate-400", Icon: Icons.Clock },
  running: { label: "Running", pill: "bg-blue-50 text-blue-700 ring-blue-600/20", dot: "bg-blue-500 animate-pulse", solid: "bg-blue-500", Icon: Icons.Spinner, spin: true },
  passed: { label: "Passed", pill: "bg-emerald-50 text-emerald-700 ring-emerald-600/20", dot: "bg-emerald-500", solid: "bg-emerald-500", Icon: Icons.Check },
  failed: { label: "Failed", pill: "bg-rose-50 text-rose-700 ring-rose-600/20", dot: "bg-rose-500", solid: "bg-rose-500", Icon: Icons.X },
  error: { label: "Error", pill: "bg-rose-50 text-rose-700 ring-rose-600/20", dot: "bg-rose-500", solid: "bg-rose-500", Icon: Icons.Alert },
  cancelled: { label: "Stopped", pill: "bg-slate-100 text-slate-600 ring-slate-500/20", dot: "bg-slate-400", solid: "bg-slate-400", Icon: Icons.Pause },
  interrupted: { label: "Interrupted", pill: "bg-amber-50 text-amber-800 ring-amber-600/30", dot: "bg-amber-500", solid: "bg-amber-500", Icon: Icons.Alert },
  /* Not run at all, with why (App Pages: a test that saves when saving is not allowed, or a placeholder left). */
  skipped: { label: "Skipped", pill: "bg-amber-50 text-amber-800 ring-amber-600/30", dot: "bg-amber-500", solid: "bg-amber-500", Icon: Icons.Minus },
  blocked: { label: "Blocked", pill: "bg-amber-50 text-amber-800 ring-amber-600/30", dot: "bg-amber-500", solid: "bg-amber-500", Icon: Icons.Alert },
  other: { label: "Not finished", pill: "bg-slate-100 text-slate-600 ring-slate-500/20", dot: "bg-slate-400", solid: "bg-slate-400", Icon: Icons.Minus },
};

function runState(ex) {
  if (!ex || ex.run_status === "ready") return "new"; // created, not executed yet
  if (ex.run_status) return TONES[ex.run_status] ? ex.run_status : "other";
  if (ex.failed > 0) return "failed";
  if (ex.total > 0 && ex.passed === ex.total) return "passed";
  return "other";
}

/* Status chip for a run, with live detail ("Running 3/8", "Queued #2"). */
export function runChip(ex) {
  const state = runState(ex);
  const t = TONES[state];
  const live = ex?.live;
  if (state === "running" && live?.state === "waiting_input") return { label: "Needs input", cls: TONES.interrupted.pill, dot: "bg-amber-500 animate-pulse" };
  let label = t.label;
  if (state === "running" && live?.total) label = `Running ${live.step}/${live.total}`;
  if (state === "queued" && live?.queue_position) label = `Queued #${live.queue_position}`;
  /* Blocked: a precondition check failed, or the run stopped before step 1
     (sign-in, the app or the page), so nothing was tested. */
  const blocked = (ex?.results || []).filter((r) => r.result_status === "blocked");
  if (state === "failed" && blocked.length) {
    label = blocked.every((r) => r.blocked_reason === "not_started") ? "Blocked · not started" : "Failed · precondition not met";
  }
  return { label, cls: t.pill, dot: t.dot };
}

/* ------------------------------------------------------------------ */
/*  Small parts                                                        */
/* ------------------------------------------------------------------ */
/* What must be true before an Artifact runs (from its requirement file). */
function PreconditionList({ items }) {
  return (
    <ul className="space-y-1">
      {items.map((p, i) => (
        <li key={i} className="flex gap-2 text-[0.8125rem] leading-snug text-slate-700">
          <Icons.JiraPrecondition className="mt-0.5 flex-none" />
          <span className="min-w-0">{p}</span>
        </li>
      ))}
    </ul>
  );
}

/* Jira item: issue-type icon + key; summary in the tooltip. */
export function LinkedChip({ kind, issueKey, summary, href, size = "sm" }) {
  const Icon = kind === "Story" ? Icons.JiraStory : kind === "Bug" ? Icons.JiraBug : Icons.JiraXrayTest;
  const cls = [
    "inline-flex flex-none items-center gap-1 font-semibold text-blue-700",
    size === "lg" ? "text-[0.8125rem]" : "text-[0.75rem]",
  ].join(" ");
  const title = `${kind} ${issueKey}${summary ? ` — ${summary}` : ""}`;
  const body = (
    <>
      <Icon />
      {issueKey}
    </>
  );
  return href ? (
    <a href={href} target="_blank" rel="noreferrer" title={title} className={`${cls} hover:underline`} onClick={(e) => e.stopPropagation()}>
      {body}
    </a>
  ) : (
    <span title={title} className={cls}>
      {body}
    </span>
  );
}

/* A trial run card's square icon button, in a tone; the Log button takes
   ICON_BUTTON, so every button on the card is one size. The same size and
   look as an Artifact card's buttons: 30px, a grey border, the tone on the
   icon and on hover. */
const ICON_BASE =
  "inline-flex h-[1.875rem] w-[1.875rem] flex-none items-center justify-center rounded-lg border border-slate-200 bg-white shadow-sm transition disabled:cursor-not-allowed disabled:opacity-50";
const ICON_TONES = {
  plain: "text-slate-500 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800",
  danger: "text-slate-400 hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600",
  run: "text-emerald-600 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700",
};
export const ICON_BUTTON = `${ICON_BASE} ${ICON_TONES.plain}`;

/* `label` names the button for screen readers when it differs from the hover. */
export function IconButton({ title, label, onClick, disabled, danger, tone, children }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={label || title}
      onClick={onClick}
      disabled={disabled}
      className={`${ICON_BASE} ${ICON_TONES[tone || (danger ? "danger" : "plain")]}`}
    >
      {children}
    </button>
  );
}

/* An issue-type icon with the latest state as a corner badge. */
/* `fit`: as tall as its content (up to the dialog height), for a dialog that
   only sometimes has much to show. */
export function Modal({ label, onClose, busy, wide, xl, md, fit = false, children }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !busy && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [busy, onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={label}>
      <div className="absolute inset-0 bg-overlay/50 backdrop-blur-sm" onClick={() => !busy && onClose()} />
      <div className={`relative z-10 flex ${fit ? "max-h-[85vh]" : DIALOG_HEIGHT} w-full flex-col overflow-hidden rounded-2xl bg-card shadow-2xl ring-1 ring-slate-200 ${xl ? "max-w-6xl" : wide ? "max-w-4xl" : md ? "max-w-2xl" : "max-w-lg"}`}>
        {children}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Model: the epic's tests with their runs                            */
/* ------------------------------------------------------------------ */
/* One entry per Xray test (a test can have several saved scripts — each one
   saved from the Requirement Board): its scripts, the ones never run (the
   "New" runs-to-be) and every run of any of its scripts. */
export function buildTests(tree, execs) {
  const byScript = new Map();
  for (const ex of execs) {
    if (!ex.testcasekey) continue;
    if (!byScript.has(String(ex.testcasekey))) byScript.set(String(ex.testcasekey), []);
    byScript.get(String(ex.testcasekey)).push(ex);
  }
  const groups = [];
  for (const s of tree?.stories || []) {
    const tests = s.tests
      .filter((t) => t.scripts.length)
      .map((t) => {
        const all = t.scripts.flatMap((sc) => byScript.get(String(sc.testcasekey)) || []);
        const byNewest = (a, b) => new Date(b.createddate) - new Date(a.createddate);
        /* Running first, then the ones waiting to be executed, then the rest — newest first. */
        const rank = (r) => (ACTIVE_RUN.has(r.run_status) ? 0 : r.run_status === "ready" ? 1 : 2);
        const runs = [...all].sort((a, b) => rank(a) - rank(b) || byNewest(a, b));
        const created = [...all].sort((a, b) => -byNewest(a, b)); // oldest first → Run 01, 02…
        const ready = runs.filter((r) => r.run_status === "ready");
        const active = runs.find((r) => ACTIVE_RUN.has(r.run_status)) || null;
        const executed = [...all].filter((r) => r.run_status !== "ready").sort(byNewest);
        const latest = active || executed[0] || ready[0] || null;
        const lastDone = executed.find((r) => !ACTIVE_RUN.has(r.run_status)) || null;
        const bug = (lastDone?.results || []).find((r) => r.bug_key) || null;
        return {
          id: `${s.key}|${t.key || t.scripts[0].testcasekey}`,
          test: t,
          story: s,
          scripts: t.scripts,
          ready,
          runs,
          runNo: (ex) => created.indexOf(ex) + 1,
          active,
          state: runState(latest),
          bug,
        };
      });
    if (tests.length) groups.push({ story: s, tests });
  }
  return groups;
}

/* ------------------------------------------------------------------ */
/*  Level 2 — the tests of the epic                                    */
/* ------------------------------------------------------------------ */
function TestItem({ t, selected, onSelect, onCreate, onSteps, onEdit, onDelete }) {
  const ref = React.useRef(null);
  /* Only a live execution (Queued / Running) shows here. Results belong to
     each execution: one Artifact can have passed in one and failed in another. */
  const chip = t.active ? runChip(t.active) : null;
  const name = t.test.summary || t.scripts[0]?.name || "";
  const obsolete = isObsolete(t.test.status);
  const warning = statusNote(t.test.key, t.test.status);
  const newest = [...t.scripts].sort((a, b) => String(b.created || "").localeCompare(String(a.created || "")))[0];
  /* Keep the chosen test in view (e.g. when opened from "Save & Execute"). */
  useEffect(() => {
    if (selected && ref.current?.scrollIntoView) ref.current.scrollIntoView({ block: "nearest" });
  }, [selected]);
  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      onClick={() => onSelect(t.id)}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && e.target === e.currentTarget && onSelect(t.id)}
      aria-current={selected ? "true" : undefined}
      className={[
        /* A card, as on the Requirement Board. */
        "group block w-full cursor-pointer rounded-xl border p-3 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/60",
        selected
          ? "border-blue-400 bg-blue-50 ring-1 ring-blue-400/40"
          : "border-slate-200 bg-card hover:border-blue-200 hover:bg-blue-50/40",
      ].join(" ")}
    >
      {/* Laid out as a trial run card: the key with the test's Jira status
          (Draft / In Review / Needs Update in the warning colour; hovering it
          says trial runs are not recorded in Jira as evidence) and priority at
          the top right; the name; then the other chips and the actions. */}
      <span className="flex items-start gap-2">
        <span className={`min-w-0 flex-1 truncate text-[0.8125rem] font-semibold tracking-tight ${selected ? "text-brand-700" : "text-blue-700"}`}>
          {t.test.key || "No test"}
        </span>
        <span className="flex flex-none flex-wrap items-center justify-end gap-1.5">
          <JiraStatusBadge status={t.test.status} category={t.test.status_category} title={warning || undefined} />
          <PriorityBadge priority={t.test.priority} />
        </span>
      </span>
      {name && (
        <span className="mt-1.5 block break-words text-[0.8125rem] leading-snug text-slate-700" title={name}>
          {name}
        </span>
      )}
      <span className="mt-2.5 flex items-center gap-2">
        <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          {chip && (
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-px text-[0.625rem] font-semibold ring-1 ring-inset ${chip.cls}`} title="A trial run in progress">
              <span className={`h-1.5 w-1.5 rounded-full ${chip.dot}`} />
              {chip.label}
            </span>
          )}
          {t.bug && <LinkedChip kind="Bug" issueKey={t.bug.bug_key} />}
        </span>
        <span className="flex flex-none items-center gap-1.5">
          {/* Log, 👁 View, 🗑 Delete, ✎ Edit, + Create: Log, View and Delete
              first, in the trial run card's order; one a card doesn't offer
              is left out, the rest following on. */}
          {newest && (
            <ActivityButton
              compact
              title={`${t.test.key || newest.name} — Artifact`}
              scope={{ testcasekey: newest.testcasekey }}
              testKey={t.test.key || ""}
            />
          )}
          {/* 👁 The generated script: the Cucumber steps next to the Playwright steps. */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSteps(t);
            }}
            title="View the Artifact and its steps."
            aria-label={`See the script of ${t.test.key || "this artifact"}`}
            className="inline-flex h-[1.875rem] w-[1.875rem] items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800"
          >
            <Icons.Eye className="h-4 w-4" />
          </button>
          {/* 🗑 With its trial runs. One that is kept (it ran in a For you
              run, or a trial of it is running) says why instead. */}
          {onDelete && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(t);
              }}
              title={keptReason({ kind: "artifact", t }) ? "This Artifact can't be deleted now: see why" : "Delete this Artifact and its trial runs"}
              aria-label={`Delete ${t.test.key || "this Artifact"}`}
              className="inline-flex h-[1.875rem] w-[1.875rem] items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-400 shadow-sm transition hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600"
            >
              <Icons.Trash className="h-4 w-4" />
            </button>
          )}
          {/* ✎ Edit the script: its steps as Playwright code. */}
          {onEdit && newest && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(t);
              }}
              title="Edit the script of this Artifact."
              aria-label={`Edit the script of ${t.test.key || "this artifact"}`}
              className="inline-flex h-[1.875rem] w-[1.875rem] items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
            >
              <Icons.Pencil className="h-4 w-4" />
            </button>
          )}
          {/* A trial runs for any status but Obsolete (the others with a warning). */}
          {/* Obsolete: a greyed-out + the size of a button (the words in its
              hover), as small as the button it stands for. */}
          {obsolete ? (
            <span
              title={trialWarning(t.test.key, t.test.status)}
              className="inline-flex h-[1.875rem] w-[1.875rem] flex-none cursor-not-allowed items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-300"
            >
              <Icons.Plus className="h-4 w-4" aria-hidden="true" />
              <span className="sr-only">Obsolete · not run</span>
            </span>
          ) : (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onCreate(t);
              }}
              title={`Create a trial run of this test. ${TRIAL_LABEL}; nothing is written to Jira.`}
              aria-label={`Create a trial run of ${t.test.key || "this test"}`}
              className="inline-flex h-[1.875rem] w-[1.875rem] items-center justify-center rounded-lg border border-slate-200 bg-white text-blue-700 shadow-sm transition hover:border-blue-300 hover:bg-blue-50"
            >
              <Icons.Plus className="h-4 w-4" />
            </button>
          )}
        </span>
      </span>
    </div>
  );
}

function TestsColumn({ groups, noEpic = false, epicEmpty = false, otherCount, selectedId, onSelect, onCreate, onSteps, onEdit, onDelete }) {
  /* No search box of its own: the screen's top search covers Artifacts. */
  const all = groups.flatMap((g) => g.tests);

  return (
    <section className="flex w-[19rem] flex-none flex-col overflow-hidden border-r border-slate-200 bg-card xl:w-[23rem] 3xl:w-[26rem] 4xl:w-[36rem]" aria-label="Tests">
      <div className={COLUMN_HEAD} data-testid="column-head">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <Icons.ScrollText className="h-5 w-5 text-brand-600" /> Artifacts
          </span>
          <span className="ml-auto rounded-full bg-white px-2 py-0.5 text-[0.6875rem] font-semibold text-slate-500 ring-1 ring-slate-200">{all.length}</span>
        </div>
      </div>

      <div className="cq-scroll min-h-0 flex-1 space-y-2 overflow-y-auto p-2.5">
        {noEpic ? (
          <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
            <Icons.ScrollText className="h-6 w-6 text-slate-300" />
            <p className="text-xs text-slate-400">Select an epic to see its artifacts.</p>
          </div>
        ) : epicEmpty ? (
          <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
            <Icons.ScrollText className="h-6 w-6 text-slate-300" />
            <p className="text-xs font-medium text-slate-600">No artifacts under this epic yet.</p>
            <p className="text-[0.6875rem] text-slate-400">Build artifacts on the Requirement Board.</p>
          </div>
        ) : all.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
            <Icons.ScrollText className="h-6 w-6 text-slate-300" />
            <p className="text-xs font-medium text-slate-600">No artifacts under this story.</p>
          </div>
        ) : (
          all.map((t) => (
            <TestItem key={t.id} t={t} selected={selectedId === t.id} onSelect={onSelect} onCreate={onCreate} onSteps={onSteps} onEdit={onEdit} onDelete={onDelete} />
          ))
        )}
        {otherCount > 0 && (
          <button
            type="button"
            onClick={() => onSelect(OTHER_RUNS)}
            className={[
              "flex w-full items-center gap-3 rounded-xl border p-3 text-left transition",
              selectedId === OTHER_RUNS
                ? "border-blue-400 bg-blue-50 ring-1 ring-blue-400/40"
                : "border-slate-200 bg-card hover:border-blue-200 hover:bg-blue-50/40",
            ].join(" ")}
          >
            <Icons.List className="h-4 w-4 flex-none text-slate-400" />
            <span className="flex-1 text-[0.75rem] font-medium text-slate-600">Other trial runs in this epic</span>
            <span className="text-[0.6875rem] tabular-nums text-slate-400">{otherCount}</span>
          </button>
        )}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Level 3 — the runs of the chosen test                              */
/* ------------------------------------------------------------------ */
function RunCard({ ex, runNo, scriptActive, busy, picked = false, pickable = false, onPick, onOpen, onWatch, onStop, onInput, onBug, onExecute, onDelete }) {
  const [value, setValue] = useState("");
  const state = runState(ex);
  const tone = TONES[state];
  const chip = runChip(ex);
  const live = ex.live;
  const active = ACTIVE_RUN.has(ex.run_status);
  const pct = live?.total ? Math.round((Math.max(0, live.step - 1) / live.total) * 100) : 0;
  /* Who raised it: its Reporter in Jira, else the Cortex user who created it
     (never the account Cortex itself calls Jira with). */
  const reporter = ex.xray_test_execution_reporter || "";
  const runBy = reporter || ex.created_by || "";
  const row = (ex.results || [])[0] || null;
  const canBug = row && row.result_status === "failed" && !row.bug_key && !active;
  /* An older trial that went to Jira (before 27 Oct 2026), or a For you run:
     read-only here. */
  const formal = ex.run_mode === "formal" || ex.assigned_run;
  const readOnly = Boolean(ex.read_only_note) || formal;
  const finished = ["passed", "failed", "error", "interrupted", "cancelled"].includes(ex.run_status);

  return (
    <article
      /* Ctrl+click (⌘+click on a Mac) on a card that can be deleted picks it
         for the header's Delete, and does nothing else: caught before its
         buttons. A link (the Bug chip) still opens in a new tab, and a card
         that can't be picked (formal, running) keeps its Ctrl+click. */
      onClickCapture={(e) => {
        if (!onPick || !pickable || !(e.ctrlKey || e.metaKey) || e.target.closest?.("a[href]")) return;
        e.preventDefault();
        e.stopPropagation();
        onPick(ex);
      }}
      data-picked={picked || undefined}
      className={[
        "relative overflow-hidden rounded-xl border bg-card transition-all duration-200",
        picked
          ? "border-brand-500 shadow-md ring-2 ring-brand-500/40"
          : active
            ? "border-blue-200 shadow-md shadow-blue-500/10 ring-1 ring-blue-500/20"
            : "border-slate-200/80 shadow-sm hover:border-slate-300 hover:shadow-md",
      ].join(" ")}
    >
      {/* The keyboard's way to pick it: a checkbox seen only while it has
          the focus (Tab to it, Space ticks it); it says whether it is picked. */}
      {onPick && pickable && (
        <input
          type="checkbox"
          aria-label={`Select ${ex.name} to delete`}
          checked={picked}
          onChange={() => onPick(ex)}
          className="sr-only focus:not-sr-only focus:absolute focus:right-2 focus:top-2 focus:z-10 focus:h-4 focus:w-4 focus:rounded focus:border-slate-400 focus:text-brand-700 focus:ring-brand-700"
        />
      )}
      <span className={`absolute inset-y-0 left-0 w-1 ${tone.solid} ${active ? "animate-pulse" : ""}`} aria-hidden="true" />
      <div className="py-3.5 pl-5 pr-4">
        {/* Top row: the name (a formal run's marks above it), and the run's
            state at the top right once it has run. */}
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            {/* The column header says these are trials, which stay in
                CortexQA: a trial card says nothing of Jira. Only a formal run
                is marked, with its Test Execution's key. */}
            {formal && (
              <span className="mb-1 flex flex-wrap items-center gap-1.5">
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-px text-[0.625rem] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
                  <Icons.BadgeCheck className="h-3 w-3" aria-hidden="true" /> Formal run: For you › Test Runs
                </span>
              </span>
            )}
            {/* Key on its own line, the name under it — as in the Epics and
                Artifacts columns. The key opens the Test Execution in Jira. */}
            {formal && ex.xray_test_execution_key && (
              <span className="flex flex-wrap items-center gap-1.5">
                <a
                  href={ex.xray_test_execution_url || undefined}
                  target="_blank"
                  rel="noreferrer"
                  title="Open the Test Execution in Jira"
                  className="block w-fit text-[0.8125rem] font-semibold tracking-tight text-blue-700 hover:underline"
                >
                  {ex.xray_test_execution_key}
                </a>
                {/* The Test Execution's own Jira status and priority. */}
                <JiraStatusBadge status={ex.xray_test_execution_status} category={ex.xray_test_execution_status_category} />
                <PriorityBadge priority={ex.xray_test_execution_priority} />
              </span>
            )}
            <button
              type="button"
              onClick={() => onOpen(ex.executionkey)}
              title="Open this trial run"
              className="mt-0.5 block max-w-full truncate text-left text-[0.8125rem] font-medium leading-snug text-slate-700 transition hover:text-brand-700"
            >
              {ex.name}
            </button>
          </div>
          {state !== "new" && (
            <span className={`inline-flex flex-none items-center gap-1.5 rounded-full px-2.5 py-1 text-[0.6875rem] font-semibold ring-1 ring-inset ${chip.cls}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${chip.dot}`} />
              {chip.label}
            </span>
          )}
        </div>
        {/* Why it is blocked, in words: the precondition not met, or what
            stopped the run before step 1. */}
        {row?.result_status === "blocked" && row.blocked_note && (
          <p className="mt-1.5 flex items-start gap-1.5 text-[0.75rem] leading-snug text-amber-800">
            <Icons.Alert className="mt-px h-3.5 w-3.5 flex-none" aria-hidden="true" />
            <span className="min-w-0 break-words">{row.blocked_note}</span>
          </p>
        )}

        {/* Bottom row: who ran it (the initials; the name on hover) and its
            Bug, then the actions. */}
        <div className="mt-2.5 flex items-center gap-2">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
            {runBy && (
              <span
                role="img"
                title={reporter && formal ? `Reporter in Jira — ${runBy}` : `Created by ${runBy}`}
                aria-label={reporter && formal ? `Reporter in Jira — ${runBy}` : `Created by ${runBy}`}
                className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-brand-600 text-[0.625rem] font-semibold uppercase text-white"
              >
                {initials(runBy)}
              </span>
            )}
            {row?.bug_key && <LinkedChip kind="Bug" issueKey={row.bug_key} href={row.bug_url || undefined} />}
          </div>
          <div className="flex flex-none items-center gap-1.5">
            {active ? (
              <>
                {onDelete && (
                  <IconButton title="This trial run can't be deleted while it runs: see why" onClick={() => onDelete(ex)} danger>
                    <Icons.Trash className="h-4 w-4" />
                  </IconButton>
                )}
                <IconButton title="Watch the live browser" onClick={() => onWatch(ex)}>
                  <Icons.Activity className="h-4 w-4" />
                </IconButton>
                <IconButton title="Stop this trial run" onClick={() => onStop(ex)} disabled={busy === "stop"} danger>
                  {busy === "stop" ? <Icons.Spinner className="h-4 w-4 animate-spin" /> : <Icons.X className="h-4 w-4" />}
                </IconButton>
              </>
            ) : (
              <>
                {/* Log, 👁 View, 🗑 Delete, Raise a defect, ▶ / ↻: Log, View
                    and Delete first, in the Artifact card's order; one a card
                    doesn't offer is left out, the rest following on. */}
                <ActivityButton compact className={ICON_BUTTON} title={ex.name} scope={{ executionkey: ex.executionkey }} />
                {/* 👁 Once executed: the result, with each step's before/after
                    screenshots. (The script itself is seen from the Artifacts
                    column.) */}
                {ex.run_status !== "ready" && (
                  <IconButton title="See the result, with each step's screenshots" onClick={() => onOpen(ex.executionkey)}>
                    <Icons.Eye className="h-4 w-4" />
                  </IconButton>
                )}
                {/* 🗑 A trial goes; a formal run says why it is kept. */}
                {onDelete && (
                  <IconButton
                    title={keptReason({ kind: "trial", ex }) ? "This run can't be deleted: see why" : "Delete this trial run"}
                    onClick={() => onDelete(ex)}
                    disabled={busy === "delete"}
                    danger
                  >
                    {busy === "delete" ? <Icons.Spinner className="h-4 w-4 animate-spin" /> : <Icons.Trash className="h-4 w-4" />}
                  </IconButton>
                )}
                {canBug && (
                  <IconButton
                    title="Raise a defect: file a Jira Bug for this failure, with the failing step's screenshots (the trial itself stays out of Jira)"
                    label="Raise a defect"
                    onClick={() => onBug(ex, row)}
                    danger
                  >
                    {/* 16px like the other icons (a Jira icon is 20px by default). */}
                    <Icons.JiraBug style={{ width: "1rem", height: "1rem" }} />
                  </IconButton>
                )}
                {/* ▶ runs a new trial; ↻ runs a finished one again (a new run on
                    the same execution). Read-only cards offer neither. */}
                {!readOnly && (ex.run_status === "ready" || finished) && (
                  <IconButton
                    title={
                      scriptActive
                        ? "This script is already running in another trial run"
                        : ex.run_status === "ready"
                          ? "Run this trial now. It stays in CortexQA: nothing is written to Jira."
                          : "Run this trial run again. Nothing is written to Jira."
                    }
                    label={ex.run_status === "ready" ? "Execute" : "Run again"}
                    onClick={() => onExecute(ex)}
                    disabled={scriptActive || busy === "execute"}
                    tone="run"
                  >
                    {busy === "execute" ? (
                      <Icons.Spinner className="h-4 w-4 animate-spin" />
                    ) : ex.run_status === "ready" ? (
                      /* No nudge: the triangle already sits a little right
                         in its box, so it reads as centred. */
                      <Icons.Play className="h-4 w-4" />
                    ) : (
                      <Icons.Refresh className="h-4 w-4" />
                    )}
                  </IconButton>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {ex.run_status === "running" && live?.total > 0 && (
        <div className="px-4 pb-3 pl-[4.375rem]">
          <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
          {live.current && (
            <p className="mt-1 truncate font-mono text-[0.625rem] text-slate-400" title={live.current}>
              {live.current}
            </p>
          )}
        </div>
      )}

      {live?.state === "waiting_input" && live.prompt && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onInput(ex, live.prompt.index, value);
            setValue("");
          }}
          className="mb-3 ml-[4.375rem] mr-4 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
        >
          <span className="text-[0.6875rem] font-medium text-amber-900">
            Step {live.prompt.index} needs a value for <span className="font-semibold">{live.prompt.field_label || live.prompt.param_name}</span>
          </span>
          <input
            type={live.prompt.masked ? "password" : "text"}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoFocus
            className="min-w-[10rem] flex-1 rounded-md border border-amber-300 bg-white px-2 py-1 text-xs text-slate-800 outline-none focus:border-amber-500"
          />
          <button type="submit" className="rounded-md bg-amber-600 px-2.5 py-1 text-[0.6875rem] font-semibold text-white hover:bg-amber-700">
            Send
          </button>
        </form>
      )}

      {!active && ex.run_error && ["error", "interrupted", "cancelled"].includes(ex.run_status) && (
        <div className="px-4 pb-3 pl-[4.375rem] text-[0.6875rem]">
          <p className="truncate text-rose-600" title={ex.run_error}>
            {ex.run_error}
          </p>
        </div>
      )}
    </article>
  );
}

export const TRIAL_RUNS_NOTE = "Trial runs are not recorded in Jira as evidence.";

/* The hover of an Artifact card's status when its Test isn't Approved, e.g.
   "Test PSR-1 is in draft. Trial runs are not recorded in Jira as evidence."
   ("" for Approved and Obsolete). */
export function statusNote(key, status) {
  if (isObsolete(status) || !trialWarning(key, status)) return "";
  return `${key ? `Test ${key}` : "This Test"} is ${statusPhrase(status)}. ${TRIAL_RUNS_NOTE}`;
}

/* What Delete asks: a trial run goes with its results and evidence; an
   Artifact with its trial runs (said only when it has any, on a line of its
   own after the question). */
export function deleteMessage(d) {
  /* Several trial runs, chosen with Ctrl+click: not named (the cards show which). */
  if (d.kind === "trials") {
    return "Are you sure you want to delete these selected trial runs? All associated test results and evidence will also be deleted.";
  }
  if (d.kind === "trial") {
    const name = d.ex?.name ? `the trial run "${d.ex.name}"` : "this trial run";
    return `Are you sure you want to delete ${name}? All associated test results and evidence will also be deleted.`;
  }
  const key = d.t.test.key || "";
  const summary = d.t.test.summary || d.t.scripts[0]?.name || "";
  const name = key && summary ? `${key} "${summary}"` : key || (summary ? `"${summary}"` : "this Artifact");
  return (
    `Are you sure you want to delete ${name}?` +
    (d.t.runs.length ? "\nThis will also permanently delete all associated trial runs, including their results and evidence." : "")
  );
}

/* The delete confirmation's checkbox (off by default): ticked, the item's
   Log goes with it (with_log); left alone, its Log stays. */
export function logOption(d, checked, onChange) {
  return {
    label: "Delete log",
    hint:
      d.kind === "trial"
        ? "Every Log entry of this trial run is deleted too. Left unticked, its Log stays."
        : d.kind === "trials"
          ? "Every Log entry of these trial runs is deleted too. Left unticked, their Log stays."
          : "Every Log entry of this Artifact and its trial runs is deleted too. Left unticked, the Log stays.",
    checked,
    onChange,
  };
}

/* What the loader lists while it deletes: each real step, in order (the
   requests, then the reloads). */
export function deleteSteps(d, withLog) {
  const evidence = withLog ? "results, screenshots and Log" : "results and screenshots";
  if (d.kind === "trial") return [`Deleting the trial run, its ${evidence}`, "Refreshing the trial runs"];
  if (d.kind === "trials") {
    return [
      ...d.exs.map((ex, i) => `Deleting "${ex.name}" (${i + 1} of ${d.exs.length}), its ${evidence}`),
      "Refreshing the trial runs",
    ];
  }
  const scripts = d.t.scripts;
  return [
    ...scripts.map((sc, i) => {
      const n = d.t.runs.filter((r) => String(r.testcasekey) === String(sc.testcasekey)).length;
      const of = scripts.length > 1 ? ` (${i + 1} of ${scripts.length})` : "";
      const what = n ? ` and its ${plural(n, "trial run")}, with their ${evidence}` : withLog ? " and its Log" : "";
      return `Deleting the Artifact${of}${what}`;
    }),
    "Refreshing the Artifacts and their Jira status",
    "Refreshing the trial runs",
  ];
}

/* The loader's list of steps: the one under way with a spinner, the
   finished ones ticked, the rest waiting. */
export function DeleteSteps({ steps }) {
  return (
    <ol className="mx-auto mt-5 max-w-sm space-y-2 text-left" aria-label="What is being done">
      {steps.map((s) => (
        <li
          key={s.label}
          className={`flex items-start gap-2.5 text-[0.8125rem] leading-snug ${
            s.state === "doing" ? "font-medium text-slate-800" : s.state === "done" ? "text-slate-500" : "text-slate-400"
          }`}
        >
          <span className="mt-px flex h-4 w-4 flex-none items-center justify-center" aria-hidden="true">
            {s.state === "done" ? (
              <Icons.Check className="h-4 w-4 text-emerald-600" />
            ) : s.state === "doing" ? (
              <Icons.Spinner className="h-3.5 w-3.5 animate-spin text-brand-600" />
            ) : (
              <span className="h-1.5 w-1.5 rounded-full bg-slate-300" />
            )}
          </span>
          <span>
            {s.label}
            <span className="sr-only">{s.state === "done" ? " (done)" : s.state === "doing" ? " (in progress)" : ""}</span>
          </span>
        </li>
      ))}
    </ol>
  );
}

/* A loader's steps from their labels, the first under way; and the setter
   that moves one on (`mark(i, "doing" | "done")`). */
export function useSteps() {
  const [steps, setSteps] = useState(null);
  const start = (labels) => setSteps(labels.map((label, i) => ({ label, state: i === 0 ? "doing" : "waiting" })));
  const mark = (i, state) => setSteps((all) => all && all.map((s, j) => (j === i ? { ...s, state } : s)));
  const stop = () => setSteps(null);
  return { steps, start, mark, stop };
}

/* What the toast and the loader call the one being deleted: an Artifact by
   its test key, a trial run by its name. */
export function deletedLabel(d) {
  if (d.kind === "trial") return `"${d.ex.name}"`;
  if (d.kind === "trials") return plural(d.exs.length, "trial run");
  return d.t.test.key || d.t.scripts[0]?.name || "The Artifact";
}

/* Why 🗑 can't delete this now, or "" when it can. An Artifact that ran in a
   For you run is kept (the server's reason, naming them); so is a formal
   run. Every trial is CortexQA's own and goes. A running one is stopped
   first. */
export function keptReason(d) {
  /* Several: the first one kept says why (one may have started since it was picked). */
  if (d.kind === "trials") {
    for (const ex of d.exs) {
      const why = keptReason({ kind: "trial", ex });
      if (why) return why;
    }
    return "";
  }
  if (d.kind === "trial") {
    const ex = d.ex;
    const name = `"${ex.name}"`;
    if (ex.run_mode === "formal" || ex.assigned_run) {
      return `${name} is a formal run (For you › Test Runs). Its result is evidence, so it can't be deleted.`;
    }
    if (ACTIVE_RUN.has(ex.run_status)) return `${name} is ${ex.run_status}. Stop it, then delete it.`;
    return "";
  }
  const t = d.t;
  const key = t.test.key || t.scripts[0]?.name || "This Artifact";
  const kept = t.scripts.find((sc) => sc.delete_blocked);
  if (kept) {
    return (
      kept.delete_blocked_reason ||
      `${key} can't be deleted: it ran in a For you run. Deleting it would delete that evidence and its screenshots.`
    );
  }
  if (t.active) return `A trial run of ${key} is ${t.active.run_status}. Stop it, then delete the Artifact.`;
  return "";
}

/* A trial run that Ctrl+click may pick for the Trial runs header's 🗑: one
   that can be deleted now (not formal, not running). */
const pickable = (ex) => !keptReason({ kind: "trial", ex });

/* A trial run's icon: the ▶ of a trial run card's Execute button (its
   green), at a column icon's 20px. `muted` greys it out for an empty
   column, as a Jira icon's `muted` does. */
export function RunIcon({ muted = false, className = "" }) {
  return (
    <Icons.Play
      data-testid="run-icon"
      aria-hidden="true"
      className={`h-5 w-5 flex-none text-emerald-600${muted ? " opacity-40 grayscale" : ""}${className ? ` ${className}` : ""}`}
    />
  );
}

/* Column header for the Executions column — same look as the Epics and
   Artifacts headers: the column's icon, the title and a count. With an
   Artifact's runs shown (`onDeletePicked`), 🗑 deletes the cards picked with
   Ctrl+click (`picked` of them), and ✕ lets them go. */
function ExecutionsHeader({ count, picked = 0, onDeletePicked, onClearPicked }) {
  return (
    <div className={COLUMN_HEAD} data-testid="column-head">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
          {/* -ml-1: the triangle starts a third into its box, so this lines
              it up with the Epics and Artifacts headers' icons. */}
          <RunIcon className="-ml-1" /> Trial runs
        </span>
        <span className="flex flex-none items-center gap-1.5">
          {picked > 0 && (
            <>
              <span className="text-[0.6875rem] font-semibold text-brand-700" aria-live="polite">
                {picked} selected
              </span>
              <IconButton title="Clear the selection (Esc)" onClick={onClearPicked}>
                <Icons.X className="h-4 w-4" />
              </IconButton>
            </>
          )}
          {onDeletePicked && (
            <IconButton
              title={
                picked
                  ? `Delete the ${plural(picked, "selected trial run")}`
                  : "Ctrl+click trial run cards to select several, then delete them here"
              }
              label={picked ? `Delete ${plural(picked, "selected trial run")}` : "Delete the selected trial runs"}
              onClick={onDeletePicked}
              disabled={!picked}
              danger
            >
              <Icons.Trash className="h-4 w-4" />
            </IconButton>
          )}
          {typeof count === "number" && (
            <span className="rounded-full bg-white px-2 py-0.5 text-[0.6875rem] font-semibold text-slate-500 ring-1 ring-slate-200">{count}</span>
          )}
        </span>
      </div>
      <p className="mt-0.5 text-[0.6875rem] text-slate-500">{TRIAL_RUNS_NOTE}</p>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Run dialog                                                         */
/* ------------------------------------------------------------------ */
/* The default execution name, e.g.
   "Automated Test Execution - PSR-1940 2026-OCT-17 09:21 PM" (local time). */
const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
export function executionName(testKey, date = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  const h = date.getHours() % 12 || 12;
  const stamp = `${date.getFullYear()}-${MONTHS[date.getMonth()]}-${pad(date.getDate())} ${pad(h)}:${pad(date.getMinutes())} ${date.getHours() < 12 ? "AM" : "PM"}`;
  return `Automated Test Execution - ${testKey || "Test"} ${stamp}`;
}

/* "Create" on a test card: name a new trial execution and create it (nothing
   in Jira); ▶ Execute on its card runs it, and again later. A Test that is
   not Approved says so first: a trial of it is not evidence. */
function CreateDialog({ t, onClose, onCreate }) {
  const warning = trialWarning(t.test.key, t.test.status);
  const [name, setName] = useState(() => executionName(t.test.key).slice(0, 300));
  const [description, setDescription] = useState("");
  /* A test can have several saved scripts — default to the newest one. */
  const scripts = [...t.scripts].sort((a, b) => String(b.created || "").localeCompare(String(a.created || "")));
  const [scriptKey, setScriptKey] = useState(scripts[0]?.testcasekey || "");
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  /* Create, or Create & Execute (the ▾ option): creates it, then runs it. */
  const create = async (execute) => {
    if (!name.trim() || !scriptKey || busy) return;
    setMenuOpen(false);
    setBusy(execute ? "execute" : "create");
    setErr("");
    const msg = await onCreate(
      { testcasekey: scriptKey, name: name.trim(), description: description.trim() },
      { execute },
    );
    setBusy("");
    if (msg) setErr(msg);
  };
  const submit = (e) => {
    e.preventDefault();
    create(false);
  };

  const inputCls =
    "w-full rounded-lg border border-slate-300 bg-card px-3 py-2 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/25";
  return (
    <Modal label="Create trial run" onClose={menuOpen ? () => setMenuOpen(false) : onClose} busy={Boolean(busy)} md>
      <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
        <div className="flex flex-none items-center gap-3 bg-brand-700 px-6 py-4 text-white">
          <Icons.Plus className="h-5 w-5 flex-none" />
          <p className="text-base font-semibold">Create Trial Run</p>
        </div>
        <div className="cq-scroll min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-6">
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[0.75rem] text-slate-600">
            <p className="font-semibold text-slate-700">{TRIAL_LABEL}</p>
            <p className="mt-0.5">It runs here and writes nothing to Jira. {EVIDENCE_POINTER}</p>
          </div>
          {warning && (
            <p role="alert" className="flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[0.75rem] text-amber-900">
              <Icons.Alert className="mt-0.5 h-3.5 w-3.5 flex-none" aria-hidden="true" /> {warning}
            </p>
          )}
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-slate-700">
              Execution name <span className="text-rose-500">*</span>
            </span>
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={300} autoFocus className={inputCls} />
          </label>
          {scripts.length > 1 && (
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-slate-700">Artifact</span>
              <select value={scriptKey} onChange={(e) => setScriptKey(e.target.value)} className={inputCls}>
                {scripts.map((sc) => (
                  <option key={sc.testcasekey} value={sc.testcasekey}>
                    {sc.name}
                    {sc.steps_count ? ` · ${plural(sc.steps_count, "step")}` : ""}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-slate-700">Description</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={6}
              placeholder="Build, environment, reason for the trial run…"
              className={inputCls}
            />
          </label>
          {err && <p className="rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">{err}</p>}
        </div>
        <div className="flex flex-none justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <button type="button" onClick={onClose} disabled={Boolean(busy)} className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60">
            Cancel
          </button>
          {/* Split button: Create, and ▾ Create & Execute. */}
          <div className="relative flex">
            <button
              type="submit"
              disabled={Boolean(busy) || !name.trim() || !scriptKey}
              className="inline-flex items-center gap-1.5 rounded-l-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-60"
            >
              {busy ? <Icons.Spinner className="h-4 w-4 animate-spin" /> : <Icons.Plus className="h-4 w-4" />}
              {busy === "execute" ? "Creating & executing…" : busy ? "Creating…" : "Create"}
            </button>
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              disabled={Boolean(busy) || !name.trim() || !scriptKey}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label="More create options"
              className="inline-flex items-center rounded-r-lg border-l border-blue-500 bg-blue-600 px-2.5 py-2 text-white shadow-sm hover:bg-blue-700 disabled:opacity-60"
            >
              <Icons.ChevronDown className="h-4 w-4" />
            </button>
            {menuOpen && (
              <div role="menu" className="absolute bottom-full right-0 z-10 mb-1.5 min-w-[11rem] overflow-hidden rounded-lg bg-card shadow-lg ring-1 ring-slate-200">
                <button
                  type="button"
                  role="menuitem"
                  autoFocus
                  onClick={() => create(true)}
                  className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-sm font-semibold text-slate-700 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-transparent"
                >
                  <Icons.Play className="h-4 w-4" /> Create &amp; Execute
                </button>
              </div>
            )}
          </div>
        </div>
      </form>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/*  👁 — the Xray test's manual steps next to the Playwright steps     */
/* ------------------------------------------------------------------ */
function ScriptStepsModal({ t, onClose }) {
  const [tc, setTc] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}testcases/${encodeURIComponent(t.sc.testcasekey)}`);
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
        if (alive) setTc(body?.data || null);
      } catch (e) {
        if (alive) setErr(e.message || "Could not load the script.");
      }
    })();
    return () => {
      alive = false;
    };
  }, [t.sc.testcasekey]);

  const xraySteps = Array.isArray(tc?.source?.xray_steps) ? tc.source.xray_steps : [];
  const steps = Array.isArray(tc?.steps) ? tc.steps : [];
  const byXray = {};
  for (const s of steps) (byXray[s.source_step_no || 0] = byXray[s.source_step_no || 0] || []).push(s);
  const unmapped = xraySteps.length ? byXray[0] || [] : steps;
  /* The steps in the order they are numbered below. */
  const ordered = [...xraySteps.flatMap((xs) => byXray[xs.step_no] || []), ...(xraySteps.length ? unmapped : [])];
  const preconditions = tc?.source?.preconditions || t.sc.preconditions || [];

  const PwStep = ({ s, n }) => (
    <li className="flex items-start gap-2 text-[0.75rem] text-slate-700">
      <span className="mt-px flex h-4 min-w-[1rem] flex-none items-center justify-center rounded bg-brand-50 px-1 text-[0.5625rem] font-semibold text-brand-700">{n}</span>
      <span className="min-w-0">
        {stepToEnglish(s)}
        {stepToScript(s) && <span className="mt-0.5 block break-all font-mono text-[0.625rem] text-slate-400">{stepToScript(s)}</span>}
      </span>
    </li>
  );

  let pwCounter = 0;
  return (
    <Modal label="Script steps" onClose={onClose} xl>
      {/* Brand-blue header bar, as on the Confirmation dialog. */}
      <div className="flex flex-none items-center gap-3 bg-brand-700 px-5 py-3.5">
        <Icons.Script className="h-5 w-5 flex-none text-white" />
        <p className="min-w-0 flex-1 truncate text-base font-semibold tracking-tight text-white">{t.sc.name}</p>
        <button type="button" onClick={onClose} aria-label="Close" className="flex-none rounded-md p-1 text-white/70 transition hover:bg-white/15 hover:text-white">
          <Icons.X className="h-5 w-5" />
        </button>
      </div>
      {(t.sc.preconditions || []).length > 0 && (
        <div className="flex-none border-b border-slate-200 px-5 py-3">
          <p className="mb-1.5 text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-400">Preconditions</p>
          <PreconditionList items={t.sc.preconditions} />
        </div>
      )}
      {(t.story.key || t.test.key) && (
        <div className="flex flex-none flex-wrap gap-4 border-b border-slate-200 px-5 py-2.5">
          {t.story.key && <LinkedChip kind="Story" issueKey={t.story.key} summary={t.story.summary} />}
          {t.test.key && <LinkedChip kind="Test" issueKey={t.test.key} summary={t.test.summary} />}
        </div>
      )}
      <div className="cq-scroll min-h-0 flex-1 overflow-y-auto p-5">
        {err ? (
          <p className="rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-700">{err}</p>
        ) : !tc ? (
          <p className="flex items-center gap-2 text-sm text-slate-500">
            <Icons.Spinner className="h-4 w-4 animate-spin" /> Loading steps…
          </p>
        ) : (
          <>
            <div className="mb-3 grid grid-cols-2 gap-4 text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-400">
              <span className="flex items-center gap-1.5">
                <Icons.JiraXrayTest /> Test steps
              </span>
              <span className="flex items-center gap-1.5">
                <Icons.Script className="h-5 w-5 text-brand-600" /> Artifacts
              </span>
            </div>
            <div className="space-y-3">
              {xraySteps.map((xs) => {
                const mapped = byXray[xs.step_no] || [];
                const preNote = mapped.length ? "" : preconditionLineNote(xs, xraySteps, ordered, preconditions, tc?.source?.precondition_lines);
                return (
                  <div key={xs.step_no} className="grid grid-cols-2 gap-4 rounded-lg border border-slate-200 p-3">
                    <div className="text-[0.75rem]">
                      <span className="mr-1.5 rounded bg-violet-100 px-1.5 py-0.5 text-[0.625rem] font-bold text-violet-700">{xs.step_no}</span>
                      {/* A Cucumber scenario line keeps its keyword, as in Jira. */}
                      {xs.keyword && <span className="font-mono font-semibold text-blue-700">{xs.keyword} </span>}
                      <span className="font-medium text-slate-800">{xs.action || "(no action)"}</span>
                      {xs.data && (
                        <p className="mt-1 text-[0.6875rem] text-slate-500">
                          <span className="font-semibold uppercase text-slate-400">Data</span> {xs.data}
                        </p>
                      )}
                      {xs.result && (
                        <p className="mt-0.5 text-[0.6875rem] text-slate-500">
                          <span className="font-semibold uppercase text-slate-400">Expect</span> {xs.result}
                        </p>
                      )}
                    </div>
                    <ol className="space-y-1.5">
                      {mapped.length ? (
                        mapped.map((s, i) => <PwStep key={i} s={s} n={++pwCounter} />)
                      ) : preNote ? (
                        <li className="flex items-start gap-1.5 text-[0.6875rem] italic text-slate-500">
                          <Icons.JiraPrecondition className="h-4 w-4" /> {preNote}
                        </li>
                      ) : (
                        <li className="text-[0.6875rem] italic text-amber-600">No Artifact step found.</li>
                      )}
                    </ol>
                  </div>
                );
              })}
              {unmapped.length > 0 && (
                <div className="grid grid-cols-2 gap-4 rounded-lg border border-dashed border-slate-200 p-3">
                  <p className="text-[0.6875rem] text-slate-400">{xraySteps.length ? "Additional steps" : "This Artifact has no test steps recorded."}</p>
                  <ol className="space-y-1.5">
                    {unmapped.map((s, i) => (
                      <PwStep key={i} s={s} n={++pwCounter} />
                    ))}
                  </ol>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */
/*  Live browser of a running run                                      */
/* ------------------------------------------------------------------ */
/* `fetchLive` lets another screen (App Pages › Executions) feed this same
   dialog: it resolves to {live, run_status}, `live` being null once the
   execution has finished. */
/* `startMaximized` opens it filling the window; the header's button switches
   between that and the dialog size. */
export function LiveRunModal({ ex, onClose, onStop, fetchLive, startMaximized = false }) {
  const [data, setData] = useState(null);
  const [maxed, setMaxed] = useState(startMaximized);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        if (fetchLive) {
          const next = await fetchLive();
          if (alive && next) setData(next);
          return;
        }
        const res = await fetch(`${API_BASE_URL}executions/${encodeURIComponent(ex.executionkey)}/live`);
        const body = await res.json().catch(() => ({}));
        if (alive && res.ok) setData(body?.data || null);
      } catch {
        /* keep the last frame */
      }
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [ex.executionkey, fetchLive]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const live = data?.live;
  const finished = data && !live;
  const result = (data?.run_status || "").toLowerCase();
  /* Close by itself shortly after the execution ends (the card shows the result). */
  const closeRef = React.useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!finished) return undefined;
    const timer = setTimeout(() => closeRef.current(), 2500);
    return () => clearTimeout(timer);
  }, [finished]);
  let status = "Starting…";
  if (finished) status = `Execution finished — ${result || "done"}`;
  else if (live?.state === "queued") status = `Queued${live.queue_position ? ` #${live.queue_position}` : ""}`;
  else if (live?.total) status = `Step ${live.step}/${live.total}`;
  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center ${maxed ? "p-2" : "p-4"}`}
      role="dialog"
      aria-modal="true"
      aria-label="Live execution"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div
        className={`relative z-10 flex w-full flex-col overflow-hidden rounded-xl bg-slate-900 shadow-2xl ring-1 ring-slate-700 ${
          maxed ? "h-full max-w-none" : `${DIALOG_HEIGHT} max-w-6xl`
        }`}
      >
        <div className="flex items-center gap-3 border-b border-slate-700 px-4 py-2.5 text-sm text-slate-200">
          <span className={`h-2.5 w-2.5 flex-none rounded-full ${finished ? "bg-slate-500" : "animate-pulse bg-blue-500"}`} />
          <span className="min-w-0 flex-1 truncate font-medium">{ex.name}</span>
          <span className="flex-none text-xs text-slate-400">{status}</span>
          {!finished && (
            <button type="button" onClick={() => onStop(ex)} className="flex-none rounded-md border border-rose-500/40 px-2 py-1 text-xs font-semibold text-rose-300 hover:bg-rose-500/10">
              Stop
            </button>
          )}
          <button
            type="button"
            onClick={() => setMaxed((m) => !m)}
            aria-label={maxed ? "Minimize" : "Maximize"}
            title={maxed ? "Minimize" : "Maximize"}
            className="flex-none rounded-md p-1 text-slate-400 hover:bg-slate-800 hover:text-slate-100"
          >
            {maxed ? <Icons.Minimize className="h-5 w-5" /> : <Icons.Maximize className="h-5 w-5" />}
          </button>
          <button type="button" onClick={onClose} aria-label="Close" className="flex-none rounded-md p-1 text-slate-400 hover:bg-slate-800 hover:text-slate-100">
            <Icons.X className="h-5 w-5" />
          </button>
        </div>
        <div className="min-h-[18rem] flex-1 overflow-auto bg-black">
          {live?.frame ? (
            <img src={live.frame} alt="Live browser" className="mx-auto block h-auto max-w-full" />
          ) : (
            <div className="flex h-72 items-center justify-center gap-2 text-sm text-slate-400">
              {finished ? (
                <span className="flex flex-col items-center gap-2 text-center">
                  <span className={`flex h-10 w-10 items-center justify-center rounded-full ${result === "passed" ? "bg-emerald-500" : result === "failed" || result === "error" ? "bg-rose-500" : "bg-slate-500"}`}>
                    {result === "passed" ? <Icons.Check className="h-5 w-5 text-white" /> : <Icons.X className="h-5 w-5 text-white" />}
                  </span>
                  <span className="text-sm font-semibold text-slate-200">The execution has finished{result ? ` — ${result}` : ""}.</span>
                  <span className="text-xs text-slate-500">Closing…</span>
                </span>
              ) : (
                <>
                  <Icons.Spinner className="h-4 w-4 animate-spin" /> Waiting for the browser…
                </>
              )}
            </div>
          )}
        </div>
        {(live?.current || (live?.log || []).length > 0) && (
          <div className="max-h-28 overflow-y-auto border-t border-slate-700 px-4 py-2 font-mono text-[0.6875rem] text-slate-400">
            {(live.log || []).map((l, i) => (
              <div key={i} className={l.startsWith("✗") ? "text-rose-400" : ""}>
                {l}
              </div>
            ))}
            {live.current && <div className="text-blue-300">▸ {live.current}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Levels 2 + 3 together                                              */
/* ------------------------------------------------------------------ */
export default function TestRunsPanel({
  epicKey,
  /* No epic chosen yet: both columns show their headers and a prompt. */
  noEpic = false,
  tree,
  /* The story chosen in the Epics column (NO_STORY for "No story"); none
     (null or left out), every story's Artifacts show. */
  storyKey,
  execs,
  otherExecs = [],
  renderOtherRuns,
  capacity,
  userkey,
  selectedId,
  onSelect,
  onOpenRun,
  onReload,
  /* After a delete, in place of onReload: told what went
     ({executionkeys} or {testcasekeys}); resolves when the runs are in. */
  onDeleted,
  setNotice,
  /* Reloads the epic's tree (after an Artifact is deleted); resolves when it is in. */
  onRefreshTree,
}) {
  const [createFor, setCreateFor] = useState(null);
  const [stepsFor, setStepsFor] = useState(null);
  const [editFor, setEditFor] = useState(null);
  const [watching, setWatching] = useState(null);
  const [bugFor, setBugFor] = useState(null);
  const [rowBusy, setRowBusy] = useState({});
  /* Why a trial was refused (an Obsolete Test), shown in the error dialog;
     "" when closed. */
  const [refused, setRefused] = useState("");
  /* The execution waiting for its variables' values ({ex, tests}), or null. */
  const [runVars, setRunVars] = useState(null);
  /* What Delete asks about: {kind: "artifact", t} or {kind: "trial", ex};
     and, for one that is kept, why ({kind, why}). Once confirmed, the one
     being deleted (the loader stays up until the lists are fresh). */
  const [deleting, setDeleting] = useState(null);
  /* "Delete log", off each time the confirmation opens. */
  const [withLog, setWithLog] = useState(false);
  const [removing, setRemoving] = useState(null);
  const progress = useSteps();
  const [showToast, toast] = useToast();
  const [kept, setKept] = useState(null);
  const askDelete = (d) => {
    const why = keptReason(d);
    if (why) setKept({ kind: d.kind, why });
    else {
      setWithLog(false);
      setDeleting(d);
    }
  };
  const [deleteBusy, setDeleteBusy] = useState(false);

  const groups = useMemo(() => buildTests(tree, execs), [tree, execs]);
  const tests = useMemo(() => groups.flatMap((g) => g.tests), [groups]);
  /* The epic's Artifacts; once a story is chosen, that story's only. */
  const storyGroups = useMemo(
    () => (storyKey ? groups.filter((g) => (g.story.key || NO_STORY) === storyKey) : groups),
    [groups, storyKey],
  );

  /* Nothing is picked for the user: executions show only once an Artifact is
     clicked (as the Requirement Board shows tests only for a clicked story). */
  const current =
    selectedId === OTHER_RUNS ? null : storyGroups.flatMap((g) => g.tests).find((t) => t.id === selectedId) || null;

  /* The trial runs picked with Ctrl+click (their executionkeys), for the
     header's 🗑. Another Artifact, ✕, Esc or their delete lets them go; one
     no longer listed (deleted, reloaded) simply drops out. */
  const [picked, setPicked] = useState(() => new Set());
  /* Only one that can be deleted is picked (`pickable`): a formal run or a
     running one isn't, and one that starts running (▶, or another user)
     lets go. */
  const shownRuns = current?.runs;
  const pickedRuns = (shownRuns || []).filter((r) => picked.has(r.executionkey) && pickable(r));
  const clearPicked = () => setPicked(new Set());
  useEffect(() => setPicked(new Set()), [current?.id]);
  useEffect(() => {
    setPicked((was) => {
      const keep = new Set((shownRuns || []).filter((r) => was.has(r.executionkey) && pickable(r)).map((r) => r.executionkey));
      return keep.size === was.size ? was : keep;
    });
  }, [shownRuns]);
  const togglePick = (ex) => {
    if (!pickable(ex)) return;
    setPicked((was) => {
      const next = new Set(was);
      if (next.has(ex.executionkey)) next.delete(ex.executionkey);
      else next.add(ex.executionkey);
      return next;
    });
  };
  const anyPicked = pickedRuns.length > 0;
  /* Esc lets the picks go, unless it closes a window over the screen (the
     Log, the live browser, a dialog): that window keeps the key. */
  useEffect(() => {
    if (!anyPicked || deleting || removing || kept) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape" || document.querySelector('[aria-modal="true"]')) return;
      setPicked(new Set());
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [anyPicked, deleting, removing, kept]);
  const deletePicked = () =>
    askDelete(
      pickedRuns.length === 1
        ? { kind: "trial", ex: pickedRuns[0], fromPick: true }
        : { kind: "trials", exs: pickedRuns, fromPick: true },
    );

  /* 👁 on an artifact: its newest script, Cucumber steps next to Playwright steps. */
  const showScript = (t) => {
    const sc = [...t.scripts].sort((a, b) => String(b.created || "").localeCompare(String(a.created || "")))[0];
    if (sc) setStepsFor({ sc, test: t.test, story: t.story });
  };
  /* ✎ on an artifact: its newest script in the editor. */
  const editScript = (t) => {
    const sc = [...t.scripts].sort((a, b) => String(b.created || "").localeCompare(String(a.created || "")))[0];
    if (sc) setEditFor({ testcasekey: sc.testcasekey, label: t.test.key || sc.name });
  };
  const activeScripts = new Set((current?.runs || []).filter((r) => ACTIVE_RUN.has(r.run_status)).map((r) => String(r.testcasekey)));

  /* Create: a trial execution (nothing in Jira), not executed yet. */
  const createExecution = async (opts, { execute: andExecute = false } = {}) => {
    try {
      const res = await fetch(`${API_BASE_URL}executions/create-for-script`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...opts, userkey: userkey || null }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        /* The gate refused (an Obsolete Test): nothing was created. Any other
           refusal shows in the Create dialog. */
        const detail = body?.detail;
        if (detail?.reason === "obsolete") {
          setCreateFor(null);
          setRefused(detail.message || "");
          return "";
        }
        return detail?.message || (typeof detail === "string" ? detail : "") || `Could not create the trial run (HTTP ${res.status}).`;
      }
      const t = createFor;
      setCreateFor(null);
      if (t) onSelect(t.id);
      if (andExecute && body?.data?.executionkey) {
        /* Create & Execute: run it straight away (asking its values first, if
           it has any); its card shows it started. */
        startExecute({ executionkey: body.data.executionkey, name: opts.name, testcasekey: opts.testcasekey });
        return "";
      }
      /* Nothing is said: the new card shows the execution. */
      onReload();
      return "";
    } catch (e) {
      return e.message || "Could not create the trial run.";
    }
  };

  /* ▶ Execute. An Artifact that keeps {VARIABLES} asks for their values first
     (for this run only — the same Artifact can run as another user next
     time); one without runs straight away. */
  const startExecute = (ex) => {
    const key = String(ex.testcasekey || "");
    for (const t of tests) {
      const sc = t.scripts.find((s) => String(s.testcasekey) === key);
      if (sc) {
        if ((sc.variables || []).length) {
          setRunVars({ ex, tests: [{ key: t.test.key, summary: t.test.summary, variables: sc.variables }] });
          return;
        }
        break;
      }
    }
    execute(ex);
  };

  /* Run a trial (new, or again once finished), with the values typed (or
     taken from the configuration) for it. */
  const execute = (ex, variables = {}) =>
    withBusy(ex.executionkey, "execute", async () => {
      const res = await fetch(`${API_BASE_URL}executions/${encodeURIComponent(ex.executionkey)}/execute`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userkey: userkey || null, variables }),
      }).catch(() => null);
      const body = res ? await res.json().catch(() => ({})) : {};
      if (!res?.ok) {
        const detail = body?.detail;
        if (detail?.reason === "obsolete") setRefused(detail.message || "");
        else setNotice(`Execute: ${(typeof detail === "string" ? detail : detail?.message) || "could not start"}`);
      }
      /* Started: its card shows Queued / Running. */
    });

  const withBusy = async (key, kind, fn) => {
    setRowBusy((b) => ({ ...b, [key]: kind }));
    try {
      await fn();
    } finally {
      setRowBusy((b) => {
        const next = { ...b };
        delete next[key];
        return next;
      });
      onReload();
    }
  };

  const stopRun = (ex) =>
    withBusy(ex.executionkey, "stop", () =>
      fetch(
        `${API_BASE_URL}executions/${encodeURIComponent(ex.executionkey)}/cancel${userkey ? `?userkey=${encodeURIComponent(userkey)}` : ""}`,
        { method: "POST" },
      ).catch(() => {}),
    );

  /* 🗑 confirmed. A trial run: that execution. An Artifact: each of its
     scripts with its trial runs (the server deletes them first, in the
     same transaction). */
  /* Delete confirmed: the dialog closes and the loader lists each step as it
     is done (the requests, then the reloads), until the columns are fresh;
     then "X has been deleted successfully." */
  const confirmDelete = async () => {
    const target = deleting;
    if (!target || deleteBusy) return;
    const log = withLog;
    setDeleteBusy(true);
    setDeleting(null);
    setRemoving(target);
    const labels = deleteSteps(target, log);
    progress.start(labels);
    const query = (...parts) => {
      const q = [...parts, userkey && `userkey=${encodeURIComponent(userkey)}`, log && "with_log=true"].filter(Boolean).join("&");
      return q ? `?${q}` : "";
    };
    /* The trial runs: the one, or each picked (one request each). */
    const runs = target.kind === "trial" ? [target.ex] : target.kind === "trials" ? target.exs : [];
    const urls = runs.length
      ? runs.map((ex) => `${API_BASE_URL}executions/${encodeURIComponent(ex.executionkey)}${query()}`)
      : target.t.scripts.map((sc) => `${API_BASE_URL}testcases/${encodeURIComponent(sc.testcasekey)}${query("with_trial_runs=true")}`);
    let deleted = true;
    /* What went, for onDeleted: the trial runs, or each script deleted (with its trial runs). */
    const gone = { executionkeys: [], testcasekeys: [] };
    /* Several picked: the one that failed by name, and how many went before
       it (the rest stay, still picked). */
    const several = target.kind === "trials";
    const failedOne = (i, why) =>
      `"${runs[i].name}" could not be deleted: ${String(why).replace(/\.$/, "")}. ` +
      `${i} of ${plural(runs.length, "trial run")} deleted; the rest are still there.`;
    let at = 0;
    try {
      for (const [i, url] of urls.entries()) {
        at = i;
        progress.mark(i, "doing");
        const res = await fetch(url, { method: "DELETE" });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          const detail = typeof body?.detail === "string" ? body.detail : "";
          setNotice(several ? failedOne(i, detail || `HTTP ${res.status}`) : detail || `Could not delete it (HTTP ${res.status}).`);
          deleted = false;
          break;
        }
        if (runs.length) gone.executionkeys.push(runs[i].executionkey);
        else gone.testcasekeys.push(target.t.scripts[i].testcasekey);
        progress.mark(i, "done");
      }
    } catch (e) {
      setNotice(several ? failedOne(at, e.message) : `Could not delete it: ${e.message}`);
      deleted = false;
    }
    if (deleted && target.fromPick) clearPicked();
    if (deleted && target.kind === "artifact" && selectedId === target.t.id) onSelect(null);
    /* The reloads: the trial runs (last step), and for an Artifact the tree
       with its tests' Jira status (the step before), side by side. */
    const reload = (i, fn) => {
      progress.mark(i, "doing");
      return Promise.resolve(fn?.()).then(() => progress.mark(i, "done"));
    };
    const last = labels.length - 1;
    try {
      await Promise.all([
        reload(last, onDeleted ? () => onDeleted(gone) : onReload),
        target.kind === "artifact" ? reload(last - 1, onRefreshTree) : null,
      ]);
    } finally {
      setRemoving(null);
      progress.stop();
      setDeleteBusy(false);
    }
    if (deleted) showToast(`${deletedLabel(target)} ${target.kind === "trials" ? "have" : "has"} been deleted successfully.`);
  };

  const sendInput = (ex, index, value) =>
    fetch(`${API_BASE_URL}executions/${encodeURIComponent(ex.executionkey)}/input`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ index, value }),
    })
      .catch(() => {})
      .finally(onReload);

  return (
    <>
      <TestsColumn
        groups={storyGroups}
        noEpic={noEpic}
        epicEmpty={tests.length === 0}
        otherCount={otherExecs.length}
        selectedId={current ? current.id : selectedId}
        onSelect={onSelect}
        onCreate={setCreateFor}
        onSteps={showScript}
        onEdit={editScript}
        onDelete={(t) => askDelete({ kind: "artifact", t })}
      />

      <section className="flex min-w-0 flex-1 flex-col overflow-hidden bg-slate-50/40" aria-label="Trial runs">
        <ExecutionsHeader
          count={current ? current.runs.length : selectedId === OTHER_RUNS ? otherExecs.length : undefined}
          picked={pickedRuns.length}
          onDeletePicked={current && current.runs.some(pickable) ? deletePicked : undefined}
          onClearPicked={clearPicked}
        />
        {selectedId === OTHER_RUNS && !current ? (
          <div className="cq-scroll min-h-0 flex-1 overflow-y-auto">
            <p className="border-b border-slate-200 bg-card px-5 py-2.5 text-xs font-semibold text-slate-600">Other trial runs in this epic</p>
            {renderOtherRuns?.(otherExecs)}
          </div>
        ) : !current ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 p-10 text-center">
            <RunIcon muted />
            <p className="text-sm text-slate-400">
              {noEpic ? "Select an epic, then an Artifact, to see its trial runs." : "Select an Artifact to see its trial runs."}
            </p>
          </div>
        ) : (
          <>
            <div className="cq-scroll min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
              {/* Running first, then the ones waiting for ▶ Execute, then the executed ones. */}
              {current.runs.map((ex) => (
                <RunCard
                  key={ex.executionkey}
                  ex={ex}
                  runNo={current.runNo(ex)}
                  scriptActive={activeScripts.has(String(ex.testcasekey))}
                  busy={rowBusy[ex.executionkey]}
                  picked={picked.has(ex.executionkey) && pickable(ex)}
                  pickable={pickable(ex)}
                  onPick={togglePick}
                  onOpen={onOpenRun}
                  onWatch={setWatching}
                  onStop={stopRun}
                  onInput={sendInput}
                  onBug={(e, row) => setBugFor({ ex: e, row })}
                  onExecute={startExecute}
                  onDelete={(e) => askDelete({ kind: "trial", ex: e })}
                />
              ))}
              {current.runs.length === 0 && (
                <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-slate-300 bg-card px-6 py-14 text-center">
                  <RunIcon muted />
                  <p className="text-sm font-semibold text-slate-700">No trial runs of {current.test.key || "this test"} yet</p>
                </div>
              )}
            </div>
          </>
        )}
      </section>

      {deleting && (
        <ConfirmModal
          title={deleting.kind === "trial" ? "Delete trial run" : deleting.kind === "trials" ? "Delete trial runs" : "Delete Artifact"}
          message={deleteMessage(deleting)}
          irreversible
          option={logOption(deleting, withLog, setWithLog)}
          busy={deleteBusy}
          onCancel={() => !deleteBusy && setDeleting(null)}
          onConfirm={confirmDelete}
        />
      )}
      {removing && (
        <Loader.Overlay variant="guide" label={`Deleting ${deletedLabel(removing)}…`} sublabel="" delay={0}>
          {progress.steps && <DeleteSteps steps={progress.steps} />}
        </Loader.Overlay>
      )}
      {toast}
      {kept && (
        <ErrorDialog
          title={kept.kind === "trial" || kept.kind === "trials" ? "This run is kept" : "This Artifact is kept"}
          icon={Icons.Lock}
          onClose={() => setKept(null)}
          message={
            <>
              <p className="mb-2">{kept.why}</p>
              <p>Nothing was deleted.</p>
            </>
          }
        />
      )}
      {createFor && <CreateDialog t={createFor} onClose={() => setCreateFor(null)} onCreate={createExecution} />}
      {runVars && (
        <RunVariablesDialog
          tests={runVars.tests}
          userkey={userkey}
          onCancel={() => setRunVars(null)}
          onExecute={(values) => {
            const { ex } = runVars;
            setRunVars(null);
            execute(ex, values);
          }}
        />
      )}
      {refused && (
        <ErrorDialog
          title="Trial run refused"
          onClose={() => setRefused("")}
          message={
            <>
              <p className="mb-2">{refused}</p>
              <p>Nothing was created or run. The refusal is in the test's Log.</p>
            </>
          }
        />
      )}
      {stepsFor && <ScriptStepsModal t={stepsFor} onClose={() => setStepsFor(null)} />}
      {editFor && (
        <ArtifactScriptEditor
          testcasekey={editFor.testcasekey}
          label={editFor.label}
          userkey={userkey}
          onClose={() => setEditFor(null)}
          onSaved={(out) => {
            const label = editFor.label;
            setEditFor(null);
            showToast(out?.changed ? `${label} has been saved successfully.` : `${label} had no changes to save.`);
            onReload?.();
          }}
        />
      )}
      {watching && <LiveRunModal ex={watching} startMaximized onClose={() => setWatching(null)} onStop={stopRun} />}
      {bugFor && (
        <RaiseBugModal
          executionkey={bugFor.ex.executionkey}
          linkkey={bugFor.row.linkkey}
          caseName={bugFor.row.name}
          onClose={() => setBugFor(null)}
          onCreated={() => onReload()}
        />
      )}
    </>
  );
}
